import { useTranslation } from 'react-i18next';
import { Table } from 'react-bootstrap';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MappedAgendaAction, MappedFieldAction } from '../../_hooks/useGetAndMapAgendaActions';

const MAX_NOTE_LENGTH = 60;

interface TableProps {
  data: MappedAgendaAction[];
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
  onViewDetail: (id: number) => void;
}

const ESITO_LABELS: Record<string, string> = {
  POSITIVO: 'Positivo',
  NEGATIVO: 'Negativo',
  NEUTRO: 'Neutro',
  ANNULLATA: 'Annullata',
};

const ESITO_ICONS: Record<string, string> = {
  POSITIVO: 'fas fa-trophy',
  NEGATIVO: 'fas fa-times-circle',
  NEUTRO: 'fas fa-comments',
  ANNULLATA: 'fas fa-ban',
};

const ESITO_BG_BY_VARIANT: Record<string, string> = {
  success: '#4caf50',
  danger: '#ef4444',
  warning: '#f59e0b',
  secondary: '#6c757d',
};

const AgendaActionsTable: React.FC<TableProps> = ({ data, onDelete, onEdit, onViewDetail }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '4rem' }}>
        {t('Nessun dato trovato')}
      </div>
    );
  }

  const header = (Object.keys(data[0]) as (keyof MappedAgendaAction)[]).filter((k) => data[0][k].visible);

  const renderCell = (row: MappedAgendaAction, key: keyof MappedAgendaAction) => {
    const field = row[key];
    const id = row['id']?.value as number;

    if (field.isStatoBadge) {
      const val = String(field.value || '');
      if (!val || val === '-') return <span>{'-'}</span>;
      return (
        <span
          style={{
            backgroundColor: '#eef0f5',
            color: '#3a3b45',
            borderRadius: '12px',
            padding: '2px 10px',
            fontSize: '0.78rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {val.replace(/_/g, ' ')}
        </span>
      );
    }

    if (field.esitoVariant) {
      const val = String(field.value);
      const icon = ESITO_ICONS[val];
      return (
        <span
          style={{
            backgroundColor: ESITO_BG_BY_VARIANT[field.esitoVariant] || '#aaa',
            color: '#fff',
            borderRadius: '12px',
            padding: '2px 10px',
            fontSize: '0.78rem',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            whiteSpace: 'nowrap',
          }}
        >
          {icon && <i className={icon} />}
          {ESITO_LABELS[val] ?? val}
        </span>
      );
    }

    if (field.isActionsDropdown) {
      const menuItems: MenuProps['items'] = [
        {
          key: 'detail',
          label: t('Visualizza dettaglio'),
          icon: <i className="icon-lens-search-check text-xl text-blue-links" />,
          onClick: () => onViewDetail(id),
        },
        {
          key: 'edit',
          label: t('Modifica'),
          icon: <i className="icon-edit text-xl text-blue-links" />,
          onClick: () => onEdit(id),
        },
        { type: 'divider' },
        {
          key: 'delete',
          label: t('Elimina'),
          icon: <i className="icon-delete text-xl" />,
          danger: true,
          onClick: () => onDelete(id),
        },
      ];

      return (
        <Tooltip title={t('Azioni')} color="white">
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <i
              className="fa-solid fa-ellipsis-vertical text-blue-links pt-2 pe-3 ps-1"
              role="button"
              onClick={(e) => e.preventDefault()}
            />
          </Dropdown>
        </Tooltip>
      );
    }

    if (field.isLink) {
      return (
        <span
          className="text-blue-links"
          role="button"
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => onViewDetail(id)}
        >
          {field.value ?? ''}
        </span>
      );
    }

    if (field.oppSfLink) {
      return (
        <a href={field.oppSfLink} target="_blank" rel="noreferrer" className="text-blue-links fw-semibold">
          {field.value ?? ''}
        </a>
      );
    }

    if (field.truncate) {
      const text = String(field.value ?? '');
      if (text.length > MAX_NOTE_LENGTH) {
        return (
          <Tooltip title={text} placement="topLeft">
            <span style={{ cursor: 'default' }}>{text.slice(0, MAX_NOTE_LENGTH)}…</span>
          </Tooltip>
        );
      }
    }

    return <span>{field.value ?? ''}</span>;
  };

  return (
    <div className="table-responsive table-bordered">
      <Table className="mt-0 mb-0">
        <thead className="bg-red">
          <tr>
            {header.map((key) => (
              <th className={data[0][key].className} key={String(key)} scope="col">
                <span>{data[0][key].label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={'row-' + index}>
              {header.map((key) => (
                <td className={data[0][key].className} key={`${String(key)}-${index}`}>
                  {renderCell(row, key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AgendaActionsTable;
